import React, { useState } from 'react';
import { Download, X, Smartphone, Monitor, Apple } from 'lucide-react';
import usePWA from '../hooks/usePWA';

interface PWAInstallerProps {
  size?: 'small' | 'large';
}

const PWAInstaller: React.FC<PWAInstallerProps> = ({ size = 'small' }) => {
  const [showInstallModal, setShowInstallModal] = useState(false);
  const { isInstallable, isIOS, isStandalone, installPWA, canInstall } = usePWA();

  const handleInstallClick = () => {
    setShowInstallModal(true);
  };

  const handleAndroidInstall = async () => {
    const result = await installPWA();
    if (result === 'accepted') {
      setShowInstallModal(false);
    }
  };

  const handleIOSInstall = () => {
    setShowInstallModal(false); 
    // Afficher les instructions iOS dans une alerte plus détaillée
    const instructions = `Pour installer CasaPresta sur votre iPhone/iPad :

1. Appuyez sur le bouton Partager (⬆️) en bas de Safari
2. Faites défiler vers le bas et appuyez sur "Sur l'écran d'accueil"
3. Modifiez le nom si souhaité, puis appuyez sur "Ajouter"
4. L'application apparaîtra sur votre écran d'accueil

Vous pourrez ensuite l'utiliser comme une application native !`;
    
    alert(instructions);
  };

  const handleDesktopInstall = async () => {
    const result = await installPWA();
    if (result === 'accepted') {
      setShowInstallModal(false);
    }
  };

  // Ne pas afficher le bouton si déjà installé
  if (isStandalone) {
    return null;
  }

  // Afficher le bouton seulement si installable
  if (!canInstall) {
    return null;
  }

  return (
    <>
      {/* Bouton d'installation */}
      <button
        onClick={handleInstallClick}
        className={
          size === 'large' 
            ? "flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            : "flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-lg"
        }
        title="Installer l'application"
      >
        <Download className={size === 'large' ? "h-6 w-6 mr-3" : "h-4 w-4 mr-2"} />
        {size === 'large' ? (
          <span>Installer l'application</span>
        ) : (
          <>
            <span className="hidden sm:inline">Installer l'app</span>
            <span className="sm:hidden">App</span>
          </>
        )}
      </button>

      {/* Modal d'installation */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Installer CasaPresta
              </h3>
              <button
                onClick={() => setShowInstallModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <img
                src="/images/casap192.png"
                alt="CasaPresta"
                className="w-16 h-16 mx-auto mb-4 rounded-xl"
              />
              <p className="text-center text-gray-600">
                Installez CasaPresta pour un accès rapide et une meilleure expérience
              </p>
            </div>

            <div className="space-y-3">
              {/* Android/Chrome */}
              {isInstallable && (
                <button
                  onClick={handleAndroidInstall}
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Smartphone className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Android / Chrome</div>
                    <div className="text-sm opacity-90">Installation automatique</div>
                  </div>
                </button>
              )}

              {/* iOS Safari */}
              {isIOS && (
                <button
                  onClick={handleIOSInstall}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Apple className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">iPhone / iPad</div>
                    <div className="text-sm opacity-90">Instructions Safari</div>
                  </div>
                </button>
              )}

              {/* Desktop */}
              {isInstallable && (
                <button
                  onClick={handleDesktopInstall}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Monitor className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Windows / Mac</div>
                    <div className="text-sm opacity-90">Application bureau</div>
                  </div>
                </button>
              )}
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                L'application sera ajoutée à votre écran d'accueil
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstaller;